import React from "react";
import { IconStarFilled } from "./coffreIcons.jsx";
import { COFFRE_FAVORITE_STAR } from "./coffreTheme.js";

/** @typedef {'image'|'pdf'|'word'|'excel'|'ppt'|'archive'|'video'|'audio'|'code'|'text'|'file'} CoffreFileIconKind */

const EXT_KIND = /** @type {Record<string, CoffreFileIconKind>} */ ({
  jpg: "image",
  jpeg: "image",
  png: "image",
  gif: "image",
  webp: "image",
  heic: "image",
  heif: "image",
  bmp: "image",
  svg: "image",
  tiff: "image",
  tif: "image",
  avif: "image",
  pdf: "pdf",
  doc: "word",
  docx: "word",
  odt: "word",
  rtf: "word",
  xls: "excel",
  xlsx: "excel",
  xlsm: "excel",
  csv: "excel",
  ods: "excel",
  ppt: "ppt",
  pptx: "ppt",
  odp: "ppt",
  zip: "archive",
  rar: "archive",
  "7z": "archive",
  tar: "archive",
  gz: "archive",
  bz2: "archive",
  mp4: "video",
  mov: "video",
  avi: "video",
  mkv: "video",
  webm: "video",
  m4v: "video",
  mpeg: "video",
  mpg: "video",
  "3gp": "video",
  mp3: "audio",
  wav: "audio",
  wave: "audio",
  flac: "audio",
  ogg: "audio",
  m4a: "audio",
  aac: "audio",
  js: "code",
  ts: "code",
  jsx: "code",
  tsx: "code",
  py: "code",
  php: "code",
  html: "code",
  htm: "code",
  css: "code",
  json: "code",
  xml: "code",
  sql: "code",
  txt: "text",
  md: "text",
  log: "text",
  eml: "text",
});

const KIND_META = /** @type {Record<CoffreFileIconKind, { label: string; accent: string }>} */ ({
  image: { label: "IMG", accent: "#0d9488" },
  pdf: { label: "PDF", accent: "#dc2626" },
  word: { label: "DOC", accent: "#2563eb" },
  excel: { label: "XLS", accent: "#16a34a" },
  ppt: { label: "PPT", accent: "#ea580c" },
  archive: { label: "ZIP", accent: "#ca8a04" },
  video: { label: "VID", accent: "#7c3aed" },
  audio: { label: "AUD", accent: "#db2777" },
  code: { label: "DEV", accent: "#4f46e5" },
  text: { label: "TXT", accent: "#64748b" },
  file: { label: "FILE", accent: "#64748b" },
});

/** Extensions affichées telles quelles sur la pastille (≤ 4 car.). */
const EXT_BADGE_LABEL = new Set([
  "jpg",
  "jpeg",
  "png",
  "gif",
  "webp",
  "heic",
  "heif",
  "bmp",
  "svg",
  "pdf",
  "doc",
  "docx",
  "xls",
  "xlsx",
  "csv",
  "ppt",
  "pptx",
  "zip",
  "rar",
  "7z",
  "mp4",
  "mp3",
  "wav",
  "wave",
  "txt",
  "md",
  "eml",
]);

function cn(...parts) {
  return parts.filter(Boolean).join(" ");
}

export function coffreFileExtension(filename) {
  const name = (filename || "").trim();
  const dot = name.lastIndexOf(".");
  if (dot < 1 || dot === name.length - 1) return "";
  return name.slice(dot + 1).toLowerCase();
}

/** @param {string} filename */
export function coffreFileIconKind(filename) {
  const ext = coffreFileExtension(filename);
  if (!ext) return "file";
  return EXT_KIND[ext] || "file";
}

function coffreFileIconMeta(filename) {
  const ext = coffreFileExtension(filename);
  if (ext === "eml") return { kind: "text", label: "EML", accent: "#4f46e5" };
  const kind = coffreFileIconKind(filename);
  const base = KIND_META[kind] || KIND_META.file;
  let label = base.label;
  if (ext && EXT_BADGE_LABEL.has(ext)) {
    label = ext === "jpeg" ? "JPG" : ext.length <= 4 ? ext.toUpperCase() : ext.slice(0, 4).toUpperCase();
  }
  return { kind, label, accent: base.accent };
}

function CoffreFileTypeSvg({ filename, className }) {
  const { label, accent } = coffreFileIconMeta(filename);
  const badge = label.length > 4 ? label.slice(0, 4) : label;

  return (
    <svg className={cn("coffre-file-type-icon", className)} viewBox="0 0 24 24" aria-hidden>
      <path
        className="coffre-file-type-icon__sheet"
        strokeWidth="1"
        d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-6z"
      />
      <path className="coffre-file-type-icon__fold" d="M14 2v6h6" />
      <rect className="coffre-file-type-icon__badge" x="5" y="14" width="14" height="7" rx="1" fill={accent} />
      <text
        x="12"
        y="19.5"
        textAnchor="middle"
        fill="#fff"
        fontSize={badge.length > 3 ? "4.8" : "5.5"}
        fontWeight="700"
        fontFamily="Poppins, system-ui, sans-serif"
        letterSpacing="0.02em"
      >
        {badge}
      </text>
    </svg>
  );
}

/**
 * Icône fichier selon l’extension (liste / grille Coffre).
 */
export function CoffreFileIconForName({ name, className = "" }) {
  return <CoffreFileTypeSvg filename={name} className={className} />;
}

/**
 * Icône fichier selon l’extension (liste / grille Coffre).
 */
export function CoffreFileTypeIcon({ entry, size = "md", className = "", gridIconPx }) {
  const isGrid = size === "grid";
  const isCompact = size === "compact";
  const isSm = size === "sm";
  const isLg = size === "lg";
  const slotClass = cn(
    isGrid && !gridIconPx && "h-[clamp(2.5rem,8vw,3.5rem)] w-[clamp(2.5rem,8vw,3.5rem)]",
    isGrid && gridIconPx && "h-full w-full",
    isCompact && "h-8 w-8",
    isLg && !isGrid && !isCompact && "h-9 w-9",
    isSm && "h-7 w-7",
    !isGrid && !isCompact && !isLg && !isSm && "h-6 w-6"
  );
  const iconSizeClass = cn(slotClass, gridIconPx && "h-full w-full", className);

  return (
    <div
      style={isGrid && gridIconPx ? { width: gridIconPx, height: gridIconPx } : undefined}
      className={cn(
        "inline-flex shrink-0 items-center justify-center overflow-visible",
        slotClass,
        isGrid && !gridIconPx && "min-h-[3.5rem] w-full max-h-full"
      )}
    >
      <CoffreFileTypeSvg filename={entry?.name} className={iconSizeClass} />
    </div>
  );
}

/** Icône fichier avec étoile favori (aligné dossiers). */
export function CoffreFileTypeIconBadge({ entry, size = "md", className = "", gridIconPx }) {
  const isGrid = size === "grid";
  const isCompact = size === "compact";
  const isSm = size === "sm";
  const isLg = size === "lg";
  const slotClass = cn(
    isGrid && !gridIconPx && "h-[clamp(2.5rem,8vw,3.5rem)] w-[clamp(2.5rem,8vw,3.5rem)]",
    isGrid && gridIconPx && "h-full w-full",
    isCompact && "h-8 w-8",
    isLg && !isGrid && !isCompact && "h-9 w-9",
    isSm && "h-7 w-7",
    !isGrid && !isCompact && !isLg && !isSm && "h-6 w-6"
  );
  const iconSizeClass = cn(slotClass, gridIconPx && "h-full w-full", className);
  const starSizeClass = cn(
    isGrid && "h-5 w-5",
    isCompact && "h-3.5 w-3.5",
    isLg && !isGrid && !isCompact && "h-4 w-4",
    isSm && "h-3 w-3",
    !isGrid && !isCompact && !isLg && !isSm && "h-3.5 w-3.5"
  );
  const favorite = Boolean(entry?.is_favorite);

  const coreIcon = <CoffreFileTypeSvg filename={entry?.name} className={iconSizeClass} />;

  if (!favorite) {
    return coreIcon;
  }

  return (
    <span
      style={isGrid && gridIconPx ? { width: gridIconPx, height: gridIconPx } : undefined}
      className={cn(
        "relative inline-flex shrink-0 items-center justify-center overflow-visible",
        slotClass,
        isGrid && gridIconPx && "h-full w-full"
      )}
      title="Favori"
    >
      {coreIcon}
      <IconStarFilled
        className={cn(
          "pointer-events-none absolute right-[-4%] top-[2%] z-[1]",
          COFFRE_FAVORITE_STAR,
          starSizeClass
        )}
        aria-hidden
      />
    </span>
  );
}
